import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Plus, 
  Calendar, 
  Clock, 
  BarChart3,
  ArrowRight,
  FileSpreadsheet,
  Zap
} from 'lucide-react';
import { plannerAPI } from '../api';
import { useCalendarStore } from '../store/calendarStore';

export default function Dashboard() {
  const { setCalendars } = useCalendarStore();

  const { data: calendars, isLoading } = useQuery({
    queryKey: ['calendars'],
    queryFn: async () => {
      const response = await plannerAPI.listCalendars();
      return response.data;
    }
  });

  useEffect(() => {
    if (calendars) {
      setCalendars(calendars);
    }
  }, [calendars, setCalendars]);

  return (
    <div className="w-full max-w-6xl mx-auto px-2 sm:px-0">
      {/* Hero section */}
      <div className="bg-gradient-to-r from-reddit-orange to-orange-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 text-white mb-6 sm:mb-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
              Reddit Mastermind
            </h1>
            <p className="text-orange-100 text-sm sm:text-base md:text-lg mb-4 max-w-xl">
              Generate AI-powered Reddit content calendars that drive engagement, 
              upvotes, and inbound leads.
            </p>
            <Link 
              to="/new" 
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-white text-reddit-orange font-medium rounded-full hover:bg-orange-50 transition-colors"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span>Create New Plan</span>
            </Link>
          </div>
          <div className="hidden lg:flex items-center justify-center">
            <div className="w-36 h-36 xl:w-48 xl:h-48 bg-white/10 rounded-full flex items-center justify-center">
              <Calendar className="w-16 h-16 xl:w-24 xl:h-24 text-white/80" />
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">CSV/XLSX Import</h3>
          <p className="text-sm text-gray-600">
            Upload your company info, personas, and keywords to generate targeted content.
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
            <Zap className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">AI-Powered Content</h3>
          <p className="text-sm text-gray-600">
            Generate natural posts and threaded comments with persona-specific voices.
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 sm:col-span-2 lg:col-span-1">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
            <BarChart3 className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Quality Scoring</h3>
          <p className="text-sm text-gray-600">
            Each post is graded for naturalness, engagement, and subreddit fit.
          </p>
        </div>
      </div>

      {/* Recent calendars */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Recent Calendars</h2>
          {calendars?.length > 0 && (
            <span className="text-sm text-gray-500">
              {calendars.length} calendar{calendars.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="p-6 sm:p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-reddit-orange border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading calendars...</p>
          </div>
        ) : calendars?.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {calendars.map((calendar) => (
              <Link
                key={calendar.id}
                to={`/calendar/${calendar.id}`}
                className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-reddit-orange" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-gray-900 truncate">
                      {calendar.company || 'Untitled Calendar'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500">
                      <span className="inline-flex items-center">
                        <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                        {new Date(calendar.createdAt).toLocaleDateString()}
                      </span>
                      {calendar.summary && (
                        <>
                          <span className="hidden sm:inline">•</span>
                          <span>{calendar.summary.total_posts} posts</span>
                          <span className="hidden sm:inline">•</span>
                          <span className="hidden sm:inline">Score: {calendar.summary.avg_score?.toFixed(1)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-6 sm:p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-1">No calendars yet</h3>
            <p className="text-gray-500 text-sm mb-4">
              Create your first content calendar to get started
            </p>
            <Link 
              to="/new" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-reddit-orange text-white font-medium rounded-full hover:bg-reddit-orangeLight transition-colors"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span>Create Calendar</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
